import React from 'react';
import SectionFeedback from './SectionFeedback.jsx';
import '../styles/QuizScoreModal.css';

const QuizScoreModal = ({
    score,
    passed,
    totalQuestions,
    correctAnswers,
    incorrectQuestions,
    isLastSection,
    nextSectionName,
    nextChapterName,
    recommendedCourse,
    showChapterProjectMessage,
    currentChapterName,
    sectionId,
    onContinue,
    onReview,
    onClose
}) => {
    const percentage = Math.round(score);
    const passingThreshold = 60;

    // Only show incorrect questions if user failed and we have the data
    const showIncorrectQuestions = !passed && incorrectQuestions && incorrectQuestions.length > 0;

    return (
        <div className="quiz-score-modal-overlay" onClick={onClose}>
            <div className="quiz-score-modal" onClick={(e) => e.stopPropagation()}>
                <button className="modal-close-btn" onClick={onClose} aria-label="Close">
                    ×
                </button>

                <div className={`score-display ${passed ? 'passed' : 'failed'}`}>
                    <div className="score-circle">
                        <svg viewBox="0 0 100 100" className="score-progress">
                            <circle
                                cx="50"
                                cy="50"
                                r="45"
                                className="score-bg"
                            />
                            <circle
                                cx="50"
                                cy="50"
                                r="45"
                                className="score-fill"
                                style={{
                                    strokeDasharray: `${(percentage / 100) * 283} 283`
                                }}
                            />
                        </svg>
                        <div className="score-text">
                            <div className="score-percentage">{percentage}%</div>
                            <div className="score-label">Score</div>
                        </div>
                    </div>
                </div>

                <div className="score-details">
                    <h2 className={`score-title ${passed ? 'passed' : 'failed'}`}>
                        {passed ? 'Great Job!' : 'Keep Practicing'}
                    </h2>

                    <p className="score-message">
                        {passed
                            ? nextSectionName
                                ? `You passed the quiz! You're ready to move on to ${nextChapterName ? `${nextChapterName}: ` : ''}${nextSectionName}.`
                                : 'You passed the quiz! Great work completing this section.'
                            : `You need ${passingThreshold}% to pass. Review the section and try again!`
                        }
                    </p>

                    <div className="score-stats">
                        <div className="stat-item">
                            <span className="stat-value">{correctAnswers}</span>
                            <span className="stat-label">Correct</span>
                        </div>
                        <div className="stat-divider">/</div>
                        <div className="stat-item">
                            <span className="stat-value">{totalQuestions}</span>
                            <span className="stat-label">Total</span>
                        </div>
                    </div>

                    {/* Show incorrect questions if user failed */}
                    {showIncorrectQuestions && (
                        <div className="incorrect-questions-box">
                            <h3>Questions You Missed</h3>
                            <p className="incorrect-questions-intro">
                                Review these questions to identify areas for improvement:
                            </p>
                            <div className="incorrect-questions-list">
                                {incorrectQuestions.map((item, index) => (
                                    <div key={index} className="incorrect-question-item">
                                        <div className="question-number">Question {index + 1}</div>
                                        <div className="question-text">{item.question}</div>
                                        <div className="question-options">
                                            {item.options.map((option, optIndex) => (
                                                <div
                                                    key={optIndex}
                                                    className={`option-item ${optIndex === item.userAnswer ? 'user-selected' : ''}`}
                                                >
                                                    <span className="option-label">
                                                        {String.fromCharCode(65 + optIndex)}.
                                                    </span>
                                                    <span className="option-text">{option}</span>
                                                    {optIndex === item.userAnswer && (
                                                        <span className="your-answer-badge">Your answer</span>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Show chapter project unlocked message */}
                    {showChapterProjectMessage && (
                        <div className="chapter-project-unlocked-box">
                            <div className="project-icon">🎉</div>
                            <h3>Chapter Project Unlocked!</h3>
                            <p>
                                You've completed all sections in <strong>{currentChapterName}</strong>!
                            </p>
                            <p style={{ fontSize: '14px', marginTop: '8px', color: '#666' }}>
                                Check out the optional hands-on project in the Chapters panel to apply what you've learned.
                            </p>
                        </div>
                    )}

                    {/* Show recommendation on last section */}
                    {isLastSection && passed && recommendedCourse && (
                        <div className="recommendation-box">
                            <h3>What's Next?</h3>
                            <p>Congratulations on completing this course!</p>
                            <p className="recommended-course">
                                <strong>Recommended:</strong> {recommendedCourse}
                            </p>
                        </div>
                    )}

                    {/* Show feedback buttons if user passed */}
                    {passed && sectionId && (
                        <SectionFeedback sectionId={sectionId} />
                    )}
                </div>

                <div className="modal-actions">
                    {passed ? (
                        <>
                            {!isLastSection && nextSectionName && (
                                <button className="btn-primary" onClick={onContinue}>
                                    Continue to {nextChapterName ? `${nextChapterName}: ` : ''}{nextSectionName} →
                                </button>
                            )}
                            {isLastSection && (
                                <button className="btn-primary" onClick={onClose}>
                                    Complete Course
                                </button>
                            )}
                        </>
                    ) : (
                        <>
                            <button className="btn-secondary" onClick={onReview}>
                                Review Section
                            </button>
                            <button className="btn-primary" onClick={onClose}>
                                Try Again
                            </button>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default QuizScoreModal;
